import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { LEAGUE } from "../../config/league";
import type {
  CloseReason,
  Direction,
  ManagerDetailDTO,
  ManagerSlotDTO,
} from "../../shared/types";
import { ReturnChart } from "../components/ReturnChart";
import { SectorDonut } from "../components/SectorDonut";
import { SocialLinks } from "../components/SocialLinks";
import {
  DirectionTag,
  ErrorBanner,
  LivePill,
  RankBadge,
  ReturnValue,
  Spinner,
  StatTile,
} from "../components/ui";
import { api } from "../lib/api";
import { managerColor } from "../lib/colors";
import {
  formatMoney,
  formatPct,
  formatTimeFromISO,
  returnColor,
  useFlash,
  usePoll,
} from "../lib/format";

export default function ManagerDetailPage() {
  const { name = "" } = useParams();
  const { data, error, loading } = usePoll(
    () => api.manager(name),
    LEAGUE.leaderboardPollSec * 1000,
    [name],
  );

  return (
    <div className="space-y-6">
      <Link to="/" className="text-sm text-muted hover:text-fg">
        ← Back to standings
      </Link>

      {error && <ErrorBanner message={error} />}
      {loading && !data && <Spinner label="Loading manager…" />}

      {data && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {data.rank && <RankBadge rank={data.rank} />}
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-extrabold text-fg">{data.name}</h1>
                  <SocialLinks name={data.name} size="md" />
                </div>
                <span className="num text-xs text-faint">seed #{data.seed}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LivePill live={data.marketOpen} />
              <div className="text-right">
                <ReturnValue value={data.avgReturn} className="text-4xl" showArrow />
                <div className="text-[11px] text-muted">average return</div>
              </div>
            </div>
          </div>

          <ManagerSummary data={data} />

          <ChartsRow data={data} name={name} />

          {/* Two aligned rows rather than two independent columns: panels in a
              row share a height, so a short one (an empty Traded away) stretches
              instead of leaving a ragged hole under the column that ends first. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <BarPanel
              title="By slot"
              blurb="What each current pick has done since it entered the slot."
              rows={currentBars(data.slots)}
            />
            <div className="lg:col-span-2">
              <ContributionChart slots={data.slots} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="flex flex-col gap-4">
              <BarPanel
                title="Discarded"
                blurb="Dropped at a rebalance — the return up to the exit price stays in that slot's score."
                rows={closedBars(data.slots, (r) => !isTrade(r))}
                empty="Nothing dropped at a rebalance yet."
              />
              <BarPanel
                title="Traded away"
                blurb="Sold to another manager — the return up to the trade price stays in this slot; the buyer starts fresh at that price."
                rows={closedBars(data.slots, isTrade)}
                empty="No trades yet."
              />
            </div>
            <div className="lg:col-span-2">
              <SeasonMoves slots={data.slots} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.slots.map((s) => (
              <SlotCard key={s.slotId} slot={s} />
            ))}
          </div>

          <p className="num text-right text-xs text-faint">
            updated {formatTimeFromISO(data.updatedAt)} ET
          </p>
        </>
      )}
    </div>
  );
}

function ManagerSummary({ data }: { data: ManagerDetailDTO }) {
  const slots = data.slots;
  const winners = slots.filter((s) => s.totalReturn > 0).length;
  const losers = slots.filter((s) => s.totalReturn < 0).length;
  const best = slots.reduce((a, b) => (b.totalReturn > a.totalReturn ? b : a), slots[0]);
  const worst = slots.reduce((a, b) => (b.totalReturn < a.totalReturn ? b : a), slots[0]);
  // A slot that has discarded something is more than its current ticker, so name
  // the slot rather than implying the new pick earned the whole number.
  const label = (s?: ManagerSlotDTO) => {
    if (!s) return "—";
    if (s.history.length) return `Slot ${s.slotIndex} incl. discards`;
    return s.open ? s.open.ticker : `Slot ${s.slotIndex}`;
  };

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile
        label="Avg return"
        value={formatPct(data.avgReturn)}
        tone={returnColor(data.avgReturn)}
        sub="across 12 slots"
      />
      <StatTile label="Winners / losers" value={`${winners} / ${losers}`} sub="positive vs negative slots" />
      <StatTile
        label="Best slot"
        value={formatPct(best?.totalReturn ?? 0)}
        tone="text-gain"
        sub={label(best)}
      />
      <StatTile
        label="Worst slot"
        value={formatPct(worst?.totalReturn ?? 0)}
        tone="text-loss"
        sub={label(worst)}
      />
    </div>
  );
}

function ChartsRow({ data, name }: { data: ManagerDetailDTO; name: string }) {
  const { data: hist } = usePoll(
    api.history,
    LEAGUE.leaderboardPollSec * 1000,
    [name],
  );

  const series = useMemo(() => {
    const m = hist?.managers.find(
      (x) => x.name.toLowerCase() === name.toLowerCase(),
    );
    return m ? [{ name: m.name, color: managerColor(m.seed), points: m.points }] : [];
  }, [hist, name]);

  const sectors = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of data.slots) {
      if (!s.open) continue;
      const sec = s.open.sector ?? "Other";
      counts.set(sec, (counts.get(sec) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([sector, value]) => ({ sector, value }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="panel p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted">
          Return over time
        </h2>
        <ReturnChart series={series} height={220} />
      </div>
      <div className="panel p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted">
          Sector exposure
        </h2>
        <SectorDonut data={sectors} />
      </div>
    </div>
  );
}

/**
 * Two stacked panels: the 12 live picks, then anything the slots discarded.
 * Keeping them apart stops a slot's banked loss from reading as a loss on the
 * name that replaced it (a flat SHOP in a slot that dropped HON at -19%).
 */
interface TimelineEvent {
  key: string;
  slot: number;
  ticker: string;
  direction: Direction;
  what: string; // how the position entered or left the slot
  detail: string; // the price it happened at
  locked: number | null; // realized return, for exits
}

const CLOSE_LABEL: Record<string, string> = {
  rebalance_discard: "dropped",
  trade_out: "traded away",
  mna_close: "acquired",
  delisting: "delisted",
};
const OPEN_LABEL: Record<string, string> = {
  rebalance_pickup: "picked up",
  trade_in: "traded in",
  mna_replace: "replacement",
};

/**
 * Every move this manager has made, newest first, grouped by the day it
 * happened. Exits carry the return they banked; entries carry the price they
 * started from. The draft itself is the last entry, so the panel reads back to
 * the start of the season.
 */
function seasonMoves(slots: ManagerSlotDTO[]): {
  date: string;
  events: TimelineEvent[];
}[] {
  const byDate = new Map<string, TimelineEvent[]>();
  const add = (date: string, e: TimelineEvent) => {
    const arr = byDate.get(date) ?? [];
    arr.push(e);
    byDate.set(date, arr);
  };
  for (const s of slots) {
    for (const h of s.history) {
      add(h.endDate, {
        key: `out-${h.positionId}`,
        slot: s.slotIndex,
        ticker: h.ticker,
        direction: h.direction,
        what: CLOSE_LABEL[h.closeReason ?? ""] ?? "closed",
        detail: h.closePrice != null ? `at ${formatMoney(h.closePrice)}` : "",
        locked: h.realizedReturn,
      });
    }
    if (s.open && s.open.openReason && s.open.openReason !== "draft") {
      add(s.open.startDate, {
        key: `in-${s.open.positionId}`,
        slot: s.slotIndex,
        ticker: s.open.ticker,
        direction: s.open.direction,
        what: OPEN_LABEL[s.open.openReason] ?? "added",
        detail: `at ${formatMoney(s.open.costBasis)}`,
        locked: null,
      });
    }
  }
  return [...byDate.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, events]) => ({
      date,
      // exits before the entries that replaced them, then by slot
      events: events.sort(
        (a, b) =>
          Number(a.locked === null) - Number(b.locked === null) || a.slot - b.slot,
      ),
    }));
}

function SeasonMoves({ slots }: { slots: ManagerSlotDTO[] }) {
  const days = seasonMoves(slots);
  const drafted = slots.filter((s) => s.open?.openReason === "draft").length;
  const draftDate = slots.find((s) => s.open?.openReason === "draft")?.open?.startDate;
  return (
    <div className="panel h-full p-4">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-muted">
        Season moves
      </h2>
      <p className="mb-3 text-[10px] text-faint">
        Every change to this roster, newest first. An exit banks its return in
        that slot; an entry starts from its price at 0%.
      </p>
      <ol className="space-y-3">
        {days.map(({ date, events }) => (
          <li key={date}>
            <div className="num mb-1 text-[10px] uppercase tracking-widest text-clock">
              {shortDate(date)}
            </div>
            <ul className="space-y-1">
              {events.map((e) => (
                <li
                  key={e.key}
                  className="num flex items-center gap-2 border-l-2 border-edge pl-2 text-xs"
                >
                  <span className="w-6 shrink-0 text-right text-[10px] text-faint">
                    {e.slot}
                  </span>
                  <span className="font-semibold text-fg">{e.ticker}</span>
                  <DirectionTag direction={e.direction} size="sm" />
                  <span className="text-muted">{e.what}</span>
                  <span className="truncate text-faint">{e.detail}</span>
                  {e.locked !== null && (
                    <span className={`ml-auto shrink-0 ${returnColor(e.locked)}`}>
                      {formatPct(e.locked)} locked
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
        {draftDate && (
          <li>
            <div className="num mb-1 text-[10px] uppercase tracking-widest text-clock">
              {shortDate(draftDate)}
            </div>
            <div className="num border-l-2 border-edge pl-2 text-xs text-muted">
              Draft — {drafted} {drafted === 1 ? "pick" : "picks"} still held
            </div>
          </li>
        )}
      </ol>
    </div>
  );
}

/** Keeps a panel on the page when a manager has made no moves of that kind. */
function EmptyPanel({ text }: { text: string }) {
  return (
    <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-edge p-3 text-center text-xs text-faint">
      {text}
    </div>
  );
}

function BarPanel({
  title,
  blurb,
  rows,
  empty,
}: {
  title: string;
  blurb: string;
  rows: BarRow[];
  empty?: string;
}) {
  return (
    <div className="panel flex h-full flex-col p-4">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-muted">
        {title}
      </h2>
      <p className="mb-3 text-[10px] text-faint">{blurb}</p>
      {rows.length > 0 ? (
        <SlotBars rows={rows} />
      ) : (
        <EmptyPanel text={empty ?? "Nothing here yet."} />
      )}
    </div>
  );
}

interface BarRow {
  key: string;
  slot: number;
  suffix: string; // "A" = the slot's current pick, "B"/"C" = ones it discarded
  ticker: string;
  value: number;
}

/** The 12 live picks, each showing only what THAT pick has done. */
function currentBars(slots: ManagerSlotDTO[]): BarRow[] {
  return slots.map((s) => ({
    key: `open-${s.slotId}`,
    slot: s.slotIndex,
    suffix: "A",
    ticker: s.open?.ticker ?? "—",
    value: s.open?.positionReturn ?? 0,
  }));
}

/**
 * Closed positions as bars, oldest first within a slot, so slot 4's first exit
 * is 4B and a later one 4C. Lettering runs across ALL of a slot's exits, then
 * `pick` splits them into the panel they belong to — a name dropped at the
 * rebalance and one sent away in a trade left the slot for different reasons,
 * but both stay banked in it.
 */
function closedBars(
  slots: ManagerSlotDTO[],
  pick: (reason: CloseReason) => boolean,
): BarRow[] {
  const rows: BarRow[] = [];
  for (const s of slots) {
    [...s.history]
      .sort((a, b) => a.endDate.localeCompare(b.endDate))
      .forEach((h, i) => {
        if (!pick(h.closeReason)) return;
        rows.push({
          key: `closed-${h.positionId}`,
          slot: s.slotIndex,
          suffix: String.fromCharCode(66 + i), // B, C, D…
          ticker: h.ticker,
          value: h.realizedReturn,
        });
      });
  }
  return rows;
}

const isTrade = (r: CloseReason) => r === "trade_out";

/** Diverging horizontal bars, sorted best→worst, centered on 0%. */
function SlotBars({ rows }: { rows: BarRow[] }) {
  const max = Math.max(0.0001, ...rows.map((r) => Math.abs(r.value)));
  const sorted = [...rows].sort((a, b) => b.value - a.value);
  return (
    <div className="space-y-1.5">
      {sorted.map((r) => {
        const pct = (Math.abs(r.value) / max) * 50;
        const pos = r.value >= 0;
        return (
          <div key={r.key} className="num flex items-center gap-2 text-xs">
            <span className="w-7 shrink-0 text-right text-[10px] text-faint">
              {r.slot}
              {r.suffix}
            </span>
            <span className="w-12 shrink-0 truncate text-right text-muted">
              {r.ticker}
            </span>
            <div className="relative h-4 flex-1 overflow-hidden rounded bg-ink-900">
              <div className="absolute left-1/2 top-0 h-full w-px bg-edge" />
              <div
                className={`absolute top-0 h-full rounded-sm ${pos ? "bg-gain/70" : "bg-loss/70"}`}
                style={{ left: pos ? "50%" : `${50 - pct}%`, width: `${pct}%` }}
              />
            </div>
            <span className={`w-16 shrink-0 text-right ${returnColor(r.value)}`}>
              {formatPct(r.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Short date for an exit line: "2026-09-20" => "Sep 20". */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Every position this slot has closed, always visible: each exit's realized
 * return is banked for the season, and the slot's score is that plus whatever
 * the current holding has done since. Spelling the sum out on the card heads off
 * the "why is my slot still red when the new name is up?" question.
 */
function LockedIn({ slot }: { slot: ManagerSlotDTO }) {
  const locked = slot.history.reduce((a, h) => a + h.realizedReturn, 0);
  const live = slot.open?.positionReturn ?? 0;
  return (
    <div className="rounded-lg border border-edge/70 bg-ink-900/30 p-3 text-xs">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted">
          Locked in
        </span>
        <span className={`num font-semibold ${returnColor(locked)}`}>
          {formatPct(locked)}
        </span>
      </div>
      <ul className="num space-y-1">
        {slot.history.map((h) => (
          <li
            key={h.positionId}
            className="flex items-center justify-between gap-2 border-t border-edge pt-1"
          >
            <span className="flex items-center gap-1.5">
              <span className="font-semibold text-fg">{h.ticker}</span>
              <DirectionTag direction={h.direction} size="sm" />
              <span className="text-[10px] text-faint">
                {shortDate(h.startDate)}–{shortDate(h.endDate)}
              </span>
            </span>
            <span className={returnColor(h.realizedReturn)}>
              {formatPct(h.realizedReturn)}
            </span>
          </li>
        ))}
      </ul>
      {slot.open && (
        <p className="num mt-2 border-t border-edge pt-1.5 text-[10px] text-muted">
          {formatPct(locked)} locked + {formatPct(live)} on {slot.open.ticker} ={" "}
          <span className={returnColor(slot.totalReturn)}>
            {formatPct(slot.totalReturn)}
          </span>
        </p>
      )}
    </div>
  );
}

interface ContributionRow {
  key: string;
  label: string; // "7B" — slot number plus its letter
  ticker: string;
  note: string; // how it left the slot, or that it is the live pick
  value: number; // share of the manager's average
  live: boolean;
}

/**
 * One row per POSITION — live picks and everything the slot has closed, each
 * carrying its return ÷ 12. Rows must be per position, not per slot: a slot
 * row labelled with its current ticker credits that ticker with a predecessor's
 * result (a flat PANW reading +5.81% that VEEV actually earned).
 */
function contributionRows(slots: ManagerSlotDTO[], n: number): ContributionRow[] {
  const rows: ContributionRow[] = [];
  for (const s of slots) {
    [...s.history]
      .sort((a, b) => a.endDate.localeCompare(b.endDate))
      .forEach((h, i) => {
        rows.push({
          key: `closed-${h.positionId}`,
          label: `${s.slotIndex}${String.fromCharCode(66 + i)}`,
          ticker: h.ticker,
          note: h.closeReason === "trade_out" ? "traded away" : "discarded",
          value: h.realizedReturn / n,
          live: false,
        });
      });
    if (s.open) {
      rows.push({
        key: `open-${s.slotId}`,
        label: `${s.slotIndex}A`,
        ticker: s.open.ticker,
        note: s.open.openReason === "draft" ? "drafted" : "current pick",
        value: s.open.positionReturn / n,
        live: true,
      });
    }
  }
  return rows.sort((a, b) => b.value - a.value);
}

/**
 * How the headline is built: every position contributes its return ÷ 12, so
 * these bars sum to the average at the top of the page. Locked-in exits are
 * solid, live picks hatched.
 */
function ContributionChart({ slots }: { slots: ManagerSlotDTO[] }) {
  const n = slots.length || LEAGUE.positionsPerManager;
  const rows = contributionRows(slots, n);
  const total = rows.reduce((a, r) => a + r.value, 0);
  const max = Math.max(0.0001, ...rows.map((r) => Math.abs(r.value)));

  return (
    <div className="panel h-full p-4">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-muted">
        How the average is built
      </h2>
      <p className="mb-3 text-[10px] text-faint">
        Every pick this season — current and closed — contributing its return ÷{" "}
        {n}. Solid = locked in, hatched = still running.
      </p>
      <div className="space-y-1.5">
        {rows.map((r) => {
          const pct = (Math.abs(r.value) / max) * 50;
          const pos = r.value >= 0;
          return (
            <div key={r.key} className="num flex items-center gap-2 text-xs">
              <span className="w-7 shrink-0 text-right text-[10px] text-faint">
                {r.label}
              </span>
              <span className="w-12 shrink-0 truncate text-right text-muted">
                {r.ticker}
              </span>
              <span className="hidden w-20 shrink-0 truncate text-[10px] text-faint sm:block">
                {r.note}
              </span>
              <div className="relative h-4 flex-1 overflow-hidden rounded bg-ink-900">
                <div className="absolute left-1/2 top-0 h-full w-px bg-edge" />
                <div
                  className={`absolute top-0 h-full rounded-sm ${pos ? "bg-gain" : "bg-loss"} ${r.live ? "opacity-60" : ""}`}
                  style={{
                    left: pos ? "50%" : `${50 - pct}%`,
                    width: `${pct}%`,
                    backgroundImage: r.live
                      ? "repeating-linear-gradient(45deg, rgba(0,0,0,0.35) 0 2px, transparent 2px 4px)"
                      : undefined,
                  }}
                />
              </div>
              <span className={`w-16 shrink-0 text-right ${returnColor(r.value)}`}>
                {formatPct(r.value)}
              </span>
            </div>
          );
        })}
      </div>
      <p className="num mt-3 flex items-center justify-between border-t border-edge pt-2 text-xs">
        <span className="text-muted">
          Sum of {rows.length} picks across {n} slots
        </span>
        <span className={`font-semibold ${returnColor(total)}`}>
          {formatPct(total)}
        </span>
      </p>
    </div>
  );
}

function SlotCard({ slot }: { slot: ManagerSlotDTO }) {
  const flash = useFlash(slot.totalReturn);
  const accent =
    slot.totalReturn > 0
      ? "border-l-2 border-l-gain/60"
      : slot.totalReturn < 0
        ? "border-l-2 border-l-loss/60"
        : "";
  return (
    <div className={`panel flex flex-col gap-3 p-4 ${accent}`}>
      <div className="flex items-center justify-between">
        <span className="num text-xs font-semibold uppercase tracking-widest text-muted">
          Slot {slot.slotIndex}
        </span>
        <span className="flex items-baseline gap-1.5">
          {slot.history.length > 0 && (
            <span className="text-[10px] uppercase tracking-widest text-faint">
              slot total
            </span>
          )}
          <span className={`rounded px-1 ${flash}`}>
            <ReturnValue value={slot.totalReturn} className="text-xl" />
          </span>
        </span>
      </div>

      {slot.open ? (
        <div className="rounded-lg border border-edge bg-ink-900/60 p-3">
          <div className="flex items-center justify-between">
            <span className="num text-lg font-bold text-fg">
              {slot.open.components?.length
                ? slot.open.components.map((c) => c.ticker).join(", ")
                : slot.open.ticker}
            </span>
            <DirectionTag direction={slot.open.direction} />
          </div>
          {slot.open.components?.length ? (
            <div className="num mt-1 text-[10px] text-clock">
              held together ·{" "}
              {slot.open.components
                .map((c) => `${c.qty} ${c.ticker}`)
                .join(" + ")}
            </div>
          ) : null}
          <dl className="num mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <Row label="Cost" value={formatMoney(slot.open.costBasis)} />
            <Row label="Now" value={formatMoney(slot.open.currentPrice)} />
            <Row
              label="Divs"
              value={formatMoney(slot.open.dividends)}
              valueClass={slot.open.dividends > 0 ? "text-gain" : "text-fg"}
            />
            <Row
              label="Return"
              value={formatPct(slot.open.positionReturn)}
              valueClass={returnColor(slot.open.positionReturn)}
            />
          </dl>
          {slot.open.spinoffValue > 0 && (
            <div className="num mt-1 text-[10px] text-clock">
              + spinoff {formatMoney(slot.open.spinoffValue)}/sh
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-edge p-3 text-center text-xs text-faint">
          {slot.history.length
            ? "closed — no open position"
            : "empty slot"}
        </div>
      )}

      {slot.history.length > 0 && <LockedIn slot={slot} />}
    </div>
  );
}

function Row({
  label,
  value,
  valueClass = "text-fg",
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <>
      <dt className="text-faint">{label}</dt>
      <dd className={`text-right ${valueClass}`}>{value}</dd>
    </>
  );
}
